import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COURSES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

export default function Students() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [q, setQ] = useState("");
  const [batchFilter, setBatchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      name: "", email: "", password: "", mobile: "",
      course: COURSES[0], batch: "", trainer: "",
      parent_name: "", parent_mobile: "", address: "",
    };
  }

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (batchFilter !== "all") params.batch = batchFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    try {
      const { data } = await api.get("/students", { params });
      setStudents(data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load students");
    }
  }, [q, batchFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/batches")
      .then(({ data }) => setBatches(data))
      .catch((err) => toast.error(formatErr(err?.response?.data?.detail) || "Failed to load batches"));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/students/${editing.id}`, form);
        toast.success("Student updated");
      } else {
        await api.post("/students", form);
        toast.success("Student added");
      }
      setOpen(false); setEditing(null); setForm(defaultForm());
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  const onEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", email: s.email || "", password: "", mobile: s.mobile || "",
      course: s.course || "", batch: s.batch || "", trainer: s.trainer || "",
      parent_name: s.parent_name || "", parent_mobile: s.parent_mobile || "", address: s.address || "",
      status: s.status || "Active",
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this student?")) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to delete student");
    }
  };

  const onMarkComplete = async (s) => {
    try {
      await api.put(`/students/${s.id}`, { status: s.status === "Completed" ? "Active" : "Completed" });
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to update status");
    }
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Student Management</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Students Directory</h1>
        </div>
        {user.role !== "student" && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(defaultForm()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Add Student
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4">
                <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="form-name" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required disabled={!!editing} testid="form-email" />
                {!editing && <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="form-password" />}
                <Field label="Mobile Number" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} required testid="form-mobile" />
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Course *</Label>
                  <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="form-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      {COURSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Batch" value={form.batch} onChange={(v) => setForm({ ...form, batch: v })} required testid="form-batch" />
                <Field label="Trainer" value={form.trainer} onChange={(v) => setForm({ ...form, trainer: v })} testid="form-trainer" />
                <Field label="Parent / Guardian Name" value={form.parent_name} onChange={(v) => setForm({ ...form, parent_name: v })} testid="form-parent-name" />
                <Field label="Parent Mobile" value={form.parent_mobile} onChange={(v) => setForm({ ...form, parent_mobile: v })} testid="form-parent-mobile" />
                <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="form-address" />
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="save-student-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
                    {editing ? "Save Changes" : "Create Student"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input
            data-testid="students-search"
            placeholder="Search by name, email, ID..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={batchFilter} onValueChange={setBatchFilter}>
          <SelectTrigger className="w-[180px]" data-testid="batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Batches</SelectItem>
            {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="w-full overflow-hidden border border-[#E2E8F0] rounded-lg bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-4 py-3">Student ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Course</th>
                <th className="px-4 py-3">Batch</th>
                <th className="px-4 py-3">Trainer</th>
                <th className="px-4 py-3">Admission</th>
                <th className="px-4 py-3">Status</th>
                {user.role !== "student" && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="students-table-body">
              {students.length === 0 && (
                <tr><td colSpan={10} className="py-12 text-center text-[#64748B]">No students found.</td></tr>
              )}
              {students.map((s) => (
                <tr key={s.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
                  <td className="px-4 py-3 font-mono-plex text-[#0F2E4A]">{s.student_id || "—"}</td>
                  <td className="px-4 py-3 font-medium text-[#0F172A]">{s.name}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.mobile}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.email}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.course}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.batch}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.trainer || "—"}</td>
                  <td className="px-4 py-3 text-[#475569]">{s.admission_date || "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => user.role !== "student" && onMarkComplete(s)}
                      data-testid={`status-${s.id}`}
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${
                        s.status === "Active" ? "bg-[#059669]/10 text-[#059669]" : "bg-[#475569]/10 text-[#475569]"
                      }`}
                    >
                      {s.status}
                    </button>
                  </td>
                  {user.role !== "student" && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => onEdit(s)} className="p-2 hover:bg-[#F1F5F9] rounded" data-testid={`edit-${s.id}`}>
                        <Pencil className="w-4 h-4 text-[#475569]" />
                      </button>
                      <button onClick={() => onDelete(s.id)} className="p-2 hover:bg-[#FEF2F2] rounded" data-testid={`delete-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-[#DC2626]" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, disabled, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#475569]">{label}{required && " *"}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="mt-1.5"
      />
    </div>
  );
}
